import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { 
  Calendar,
  Clock,
  Eye,
  Heart,
  MessageCircle,
  Grid3x3,
  List,
  Search,
  TrendingUp,
  Filter,
  ChevronRight,
  User,
  Tag
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getImageUrl = (url) => {
  if (!url) return "/api/images/hero_culture_baoule.png";
  if (url.startsWith("http")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

export default function Articles() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [filter, setFilter] = useState('all'); // 'all', 'recent', 'popular'
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetch(`${API}/cms/homepage`)
      .then(res => res.json())
      .then(data => {
        setArticles(data.articles || []);
        setFilteredArticles(data.articles || []);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    let result = [...articles];
    
    // Filter by category
    if (categoryFilter !== 'all') {
      result = result.filter(article => article.category === categoryFilter);
    }
    
    // Filter by search
    if (searchQuery) {
      result = result.filter(article => 
        article.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.excerpt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.category?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Sort by filter
    switch (filter) {
      case 'recent':
        result.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
        break;
      case 'popular':
        result.sort((a, b) => (parseInt(b.views) || 0) - (parseInt(a.views) || 0));
        break;
      default:
        break;
    }
    
    setFilteredArticles(result);
  }, [filter, categoryFilter, searchQuery, articles]);

  // Get unique categories
  const categories = ['all', ...new Set(articles.map(article => article.category).filter(Boolean))];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2">
              📰 Actualités & Articles
            </h1>
            <p className="text-muted-foreground">
              Découvrez nos derniers articles sur la culture baoulé
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 p-4 rounded-xl">
              <p className="text-2xl font-bold text-primary">{articles.length}</p>
              <p className="text-sm text-muted-foreground">Articles</p>
            </div>
            <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 p-4 rounded-xl">
              <p className="text-2xl font-bold text-secondary">{categories.length - 1}</p>
              <p className="text-sm text-muted-foreground">Catégories</p>
            </div>
            <div className="bg-gradient-to-br from-accent/10 to-accent/5 p-4 rounded-xl">
              <p className="text-2xl font-bold text-accent">
                {articles.reduce((acc, a) => acc + (parseInt(a.views) || 0), 0)}
              </p>
              <p className="text-sm text-muted-foreground">Lectures</p>
            </div>
            <div className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 p-4 rounded-xl">
              <p className="text-2xl font-bold text-orange-500">
                {articles.filter(a => {
                  const date = new Date(a.date);
                  const weekAgo = new Date();
                  weekAgo.setDate(weekAgo.getDate() - 7);
                  return date > weekAgo;
                }).length}
              </p>
              <p className="text-sm text-muted-foreground">Cette semaine</p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Rechercher un article..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Filters & View Mode */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            {/* Category Filter */}
            {categories.length > 1 && (
              <div className="flex-1">
                <div className="flex items-center gap-2 overflow-x-auto pb-2">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setCategoryFilter(cat)}
                      className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                        categoryFilter === cat
                          ? 'bg-primary text-white'
                          : 'bg-muted hover:bg-muted/80 text-foreground'
                      }`}
                    >
                      {cat === 'all' ? 'Toutes' : cat}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Sort & View Options */}
            <div className="flex items-center gap-2">
              {/* Sort Filter */}
              <div className="flex items-center gap-2 bg-muted p-1 rounded-lg">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-2 rounded text-sm font-medium transition-colors ${
                    filter === 'all' ? 'bg-primary text-white' : 'hover:bg-background'
                  }`}
                >
                  Tout
                </button>
                <button
                  onClick={() => setFilter('recent')}
                  className={`px-3 py-2 rounded text-sm font-medium transition-colors flex items-center gap-1 ${
                    filter === 'recent' ? 'bg-primary text-white' : 'hover:bg-background'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  Récent
                </button>
                <button
                  onClick={() => setFilter('popular')}
                  className={`px-3 py-2 rounded text-sm font-medium transition-colors flex items-center gap-1 ${
                    filter === 'popular' ? 'bg-primary text-white' : 'hover:bg-background'
                  }`}
                >
                  <TrendingUp className="w-4 h-4" />
                  Populaire
                </button>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center gap-2 bg-muted p-1 rounded-lg">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'grid' ? 'bg-primary text-white' : 'hover:bg-background'
                  }`}
                  title="Grille"
                >
                  <Grid3x3 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded transition-colors ${
                    viewMode === 'list' ? 'bg-primary text-white' : 'hover:bg-background'
                  }`}
                  title="Liste"
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          {/* Articles Grid/List */}
          {filteredArticles.length > 0 ? (
            <div className={
              viewMode === 'grid' 
                ? 'grid md:grid-cols-2 lg:grid-cols-3 gap-8' 
                : 'space-y-6'
            }>
              {filteredArticles.map((article, i) => (
                <article 
                  key={article.id || i} 
                  className={`article-card animate-slide-up cursor-pointer group ${
                    viewMode === 'list' ? 'flex gap-6 items-start' : ''
                  }`}
                  style={{animationDelay: `${i * 0.05}s`}}
                  onClick={() => navigate(`/article/${article.id}`)}
                >
                  {/* Image */}
                  <div className={`relative overflow-hidden rounded-xl ${
                    viewMode === 'list' ? 'w-80 flex-shrink-0' : ''
                  }`}>
                    <img 
                      src={getImageUrl(article.image_url)} 
                      alt={article.title} 
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300" 
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-foreground px-4 py-2 rounded-full font-semibold text-sm">
                        Lire l'article
                      </span>
                    </div>

                    {/* Category Badge */}
                    {article.category && (
                      <div className="absolute top-3 left-3 bg-primary px-2 py-1 rounded text-xs font-semibold text-white">
                        {article.category}
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className={`p-6 ${viewMode === 'grid' ? '' : 'flex-1'}`}>
                    <div className="text-primary font-bold text-sm uppercase mb-2">
                      {article.category}
                    </div>
                    
                    <h3 className="font-heading text-xl font-bold mb-3 group-hover:text-primary transition-colors line-clamp-2">
                      {article.title}
                    </h3>
                    
                    <p className="text-muted-foreground mb-4 line-clamp-3">
                      {article.excerpt}
                    </p>

                    {/* Meta */}
                    <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                      {article.author && (
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-xs font-bold">
                            {article.author.charAt(0)}
                          </div>
                          <span>{article.author}</span>
                        </div>
                      )}
                      
                      {article.time && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {article.time}
                        </span>
                      )}
                      
                      {article.read_time && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {article.read_time}
                        </span>
                      )}
                    </div>

                    {/* Stats (if in list mode) */}
                    {viewMode === 'list' && (
                      <div className="flex items-center gap-4 mt-4 pt-4 border-t border-border text-sm text-muted-foreground">
                        {article.views && (
                          <span className="flex items-center gap-1">
                            <Eye className="w-4 h-4" />
                            {article.views}
                          </span>
                        )}
                        {article.likes && (
                          <span className="flex items-center gap-1">
                            <Heart className="w-4 h-4" />
                            {article.likes}
                          </span>
                        )}
                        {article.comments && (
                          <span className="flex items-center gap-1">
                            <MessageCircle className="w-4 h-4" />
                            {article.comments}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-24 h-24 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                <Search className="w-12 h-12 text-muted-foreground" />
              </div>
              <h3 className="font-heading text-xl font-bold mb-2">
                {searchQuery 
                  ? `Aucun article trouvé pour "${searchQuery}"`
                  : categoryFilter !== 'all' 
                    ? `Aucun article dans "${categoryFilter}"`
                    : "Aucun article pour le moment"
                }
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery || categoryFilter !== 'all'
                  ? 'Essayez une autre recherche ou catégorie'
                  : 'Les articles seront bientôt disponibles !'
                }
              </p>
              {(searchQuery || categoryFilter !== 'all') && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setCategoryFilter('all');
                  }}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
